import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getCurrentUser,
  getProfile,
  getUserTodos,
  updateProfile,
  getLibraryEntry,
} from '../utils/supabase';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [todos, setTodos] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [stats, setStats] = useState({
    totalTime: 0,
    weekTime: 0,
    monthTime: 0,
    completedTodos: 0,
    totalTodos: 0,
  });
  const [error, setError] = useState('');

  const isOwnProfile = !id || id === user?.id;

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    setLoading(true);
    const { user } = await getCurrentUser();
    setUser(user);

    const profileId = id || user?.id;
    if (!profileId) {
      setLoading(false);
      return;
    }

    const { data: profileData } = await getProfile(profileId);
    if (!profileData) {
      setError('Profile not found');
      setLoading(false);
      return;
    }
    setProfile(profileData);
    setEditName(profileData.full_name || '');

    // Load user's todos
    const { data: todosData } = await getUserTodos(profileId);
    setTodos(todosData || []);

    // Calculate stats
    await calculateStats(profileId, todosData || []);

    setLoading(false);
  };

  const calculateStats = async (userId, todosList) => {
    const now = new Date();
    const weekStart = startOfWeek(now);
    const weekEnd = endOfWeek(now);
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    // Get library data
    const { data: libraryData } = await getLibraryEntry(userId);
    let totalSeconds = 0;
    let weekSeconds = 0;
    let monthSeconds = 0;

    if (libraryData?.time_spent) {
      const timeSpent = typeof libraryData.time_spent === 'string'
        ? JSON.parse(libraryData.time_spent)
        : libraryData.time_spent;

      // Calculate total time from all sessions
      for (const [key, seconds] of Object.entries(timeSpent)) {
        totalSeconds += seconds;
        const date = new Date(key);
        if (isWithinInterval(date, { start: weekStart, end: weekEnd })) {
          weekSeconds += seconds;
        }
        if (isWithinInterval(date, { start: monthStart, end: monthEnd })) {
          monthSeconds += seconds;
        }
      }
    }

    // Calculate todo stats
    const completed = todosList.filter(t => t.is_completed).length;
    const total = todosList.length;

    setStats({
      totalTime: totalSeconds,
      weekTime: weekSeconds,
      monthTime: monthSeconds,
      completedTodos: completed,
      totalTodos: total,
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handleUpdateProfile = async () => {
    if (!editName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    const { data, error } = await updateProfile(user.id, {
      full_name: editName.trim(),
      updated_at: new Date().toISOString(),
    });

    if (error) {
      setError(error.message);
      return;
    }

    setProfile(data);
    setEditing(false);
    setError('');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">😕</div>
          <p className="text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">👤</div>
          <p className="text-gray-500">Profile not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] pb-24">
      <div className="container-custom py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold flex-shrink-0">
              {getInitials(profile.full_name)}
            </div>
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your name"
                  />
                  <button
                    onClick={handleUpdateProfile}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold">{profile.full_name}</h1>
                  {isOwnProfile && (
                    <button
                      onClick={() => setEditing(true)}
                      className="text-sm text-primary hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
              <p className="text-gray-500 text-sm">{profile.email}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  Member since {new Date(profile.created_at).toLocaleDateString()}
                </span>
                {isOwnProfile && (
                  <Link
                    to="/profile/todo"
                    className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full hover:bg-primary/20 transition"
                  >
                    View my todos →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">Total Time</p>
            <p className="text-2xl font-bold text-primary">{formatTime(stats.totalTime)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">This Week</p>
            <p className="text-2xl font-bold text-secondary">{formatTime(stats.weekTime)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">This Month</p>
            <p className="text-2xl font-bold text-green-500">{formatTime(stats.monthTime)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">Todos Done</p>
            <p className="text-2xl font-bold text-orange-500">
              {stats.completedTodos}/{stats.totalTodos}
            </p>
          </div>
        </div>

        {/* Recent Todos */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-4">📋 Recent Todos</h2>
          {todos.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No todos yet</p>
          ) : (
            <div className="space-y-3">
              {todos.slice(0, 5).map((todo) => (
                <div
                  key={todo.id}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    todo.is_completed ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  <span className="text-lg">
                    {todo.is_completed ? '✅' : '📝'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`${todo.is_completed ? 'line-through text-gray-500' : ''}`}>
                      {todo.content}
                    </p>
                    <p className="text-xs text-gray-400">
                      {format(new Date(todo.todo_date + 'T00:00:00'), 'MMM d, yyyy')}
                      {todo.is_completed && ' • Completed'}
                    </p>
                  </div>
                </div>
              ))}
              {todos.length > 5 && (
                <Link
                  to="/profile/todo"
                  className="block text-center text-primary hover:underline text-sm"
                >
                  View all {todos.length} todos →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
